import * as Sentry from '@sentry/react';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { Project } from '@shared/models/project.model';
import { getAllBuildingVersions } from '@shared/helpers/recursive-element.helpers';

/** Sentry message used for every optimistic-lock (422) race event. */
export const RACE_CONDITION_SENTRY_MESSAGE =
  'Project save 422 (optimistic-lock race)';

/** Keep breadcrumb/event payloads small so Sentry does not truncate them. */
const MAX_ELEMENT_IDS = 10;

/** Compact per-save context recorded as a breadcrumb before each persist attempt. */
export type ProjectSaveBreadcrumb = {
  kind: 'changes' | 'snapshot';
  projectId: number;
  /** `updated_at` the save was built on — the value the optimistic lock compares. */
  baselineUpdatedAt?: string;
  /** Change discriminants (only for `changes` saves) — the "which flow" signal. */
  changeTypes?: string[];
  changeCount?: number;
  /** First few target ids; capped to keep the breadcrumb small. */
  elementIds?: string[];
};

/** Reads the target id a {@link ProjectChange} touches, if any. */
const changeTargetId = (change: ProjectChange): string | undefined => {
  if ('elementId' in change && typeof change.elementId === 'string') {
    return change.elementId;
  }
  if ('versionId' in change && typeof change.versionId === 'string') {
    return change.versionId;
  }
  return undefined;
};

/** Builds a compact {@link ProjectSaveBreadcrumb} payload from a `changes` save. */
export const summarizeChangesForBreadcrumb = (
  changes: ProjectChange[],
): Pick<
  ProjectSaveBreadcrumb,
  'changeTypes' | 'changeCount' | 'elementIds'
> => ({
  changeTypes: [...new Set(changes.map((change) => change.type))],
  changeCount: changes.length,
  elementIds: [
    ...new Set(
      changes
        .map(changeTargetId)
        .filter((id): id is string => id !== undefined),
    ),
  ].slice(0, MAX_ELEMENT_IDS),
});

/**
 * Records a compact breadcrumb for a project save attempt. Attaches automatically
 * to any Sentry event captured afterwards, giving the exact flow that led to it.
 */
export const recordProjectSaveBreadcrumb = (
  data: ProjectSaveBreadcrumb,
): void =>
  Sentry.addBreadcrumb({
    category: 'project-save',
    level: 'info',
    message: `save:${data.kind}`,
    data,
  });

/** Session-scoped dedupe so each deactivated change is reported once, not on every enrich. */
const reportedInactiveChangeKeys = new Set<string>();

/**
 * Records one breadcrumb per stored proposal change the enrich pipeline marked
 * {@link ProjectChange.inactive} (stale positional reference degraded instead of
 * write-locking the save, DEV-3640). Deduped per session on
 * `versionId:actionId:changeType:elementId:at`.
 */
export const reportInactiveProposalChanges = (project: Project): void => {
  for (const version of getAllBuildingVersions(project)) {
    for (const action of version.actions ?? []) {
      for (const change of action.changes) {
        if (!change.inactive) continue;
        const elementId = 'elementId' in change ? change.elementId : undefined;
        const key = `${version.id}:${action.id}:${change.type}:${elementId}:${change.inactive.at}`;
        if (reportedInactiveChangeKeys.has(key)) continue;
        reportedInactiveChangeKeys.add(key);
        Sentry.addBreadcrumb({
          category: 'proposal-actions',
          level: 'warning',
          message: 'stale-change-deactivated',
          data: {
            versionId: version.id,
            actionId: action.id,
            changeType: change.type,
            elementId,
            reason: change.inactive.reason,
            at: change.inactive.at,
          },
        });
      }
    }
  }
};

/**
 * Captures an optimistic-lock (422) race as a Sentry event so the breadcrumb
 * trail surfaces. `warning` when recovery persisted the user's edit, `error`
 * when recovery had to reset to server truth (the edit was dropped).
 */
export const captureRaceCondition = (
  level: 'warning' | 'error',
  context: Record<string, unknown>,
): void =>
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('race_condition', 'true');
    scope.setExtras(context);
    Sentry.captureMessage(RACE_CONDITION_SENTRY_MESSAGE);
  });
